import React, { useCallback, useEffect, useRef } from "react"
import {
  DefaultThreadMessage,
  StyledThreadsList,
  ThreadCardContainer,
  ThreadSideTick,
  ThreadSubjectAndDate
} from "./Threads"
import { CenteredLoadingIndicator } from "../views/molecules/Molecules"
import { InAppMessagingCreateNewThreadForm, NewMessageButtonContainer } from "./NewThread"
import { MessagingErrorContainer, ThreadMessagesContainer } from "./Messages"
import { InAppMessagingThread } from "./inAppMessagingTypes"
import { MessageReplyContainer } from "./MessageReplyContainer"
import { useInAppMessaging } from "../context/messaging/InAppMessagingContext"
import useGetThreads from "./hooks/useGetThreads"
import { useHistory } from "react-router"
import { useParams } from "react-router-dom"
import { useIntl } from "react-intl"
import { Box, Grid, Stack, Theme, useMediaQuery, useTheme } from "@carrotfertility/carotene-core"
import { useCarrotCompanionCarrotPlanMessage } from "#/components/care-team/hooks/useCarrotCompanion"
import { updateCarrotPlanMessageDismissTimestamp } from "#/components/views/modal/utils/localStorageUtils"

function DefaultThreadCard(): JSX.Element {
  const { selectedThread, setSelectedThread, showNewThreadForm, setShowNewThreadForm, setShowOnlyThreadsList } =
    useInAppMessaging()
  const intl = useIntl()

  return (
    <Box sx={{ marginBlockEnd: "138px", borderBlockEnd: "1px solid rgba(0, 0, 0, 0.1)" }}>
      <ThreadCardContainer
        key={-1}
        onClick={() => {
          // canned response
          setSelectedThread({ threadId: -1 })
          setShowNewThreadForm(false)
          setShowOnlyThreadsList(false)
        }}
      >
        <ThreadSideTick active={selectedThread?.threadId === -1 && !showNewThreadForm} />
        <ThreadSubjectAndDate
          thread={{
            date: intl.formatMessage({ defaultMessage: "From Carrot" }),
            subject: intl.formatMessage({ defaultMessage: "Get started with Messages" })
          }}
        />
      </ThreadCardContainer>
    </Box>
  )
}

function MemberMessagesContainer(): JSX.Element {
  const { thread: threadParam } = useParams<{ thread: string }>()
  const history = useHistory()
  const hideColumn = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"))
  const intl = useIntl()

  const {
    showNewThreadForm,
    setShowNewThreadForm,
    setShowOnlyThreadsList,
    showOnlyThreadsList,
    selectedThread,
    setSelectedThread
  } = useInAppMessaging()

  const {
    data: threadsData,
    isLoading: isGetThreadsLoading,
    isError: isGetThreadsError,
    isSuccess: isGetThreadsSuccess
  } = useGetThreads()

  const { data: cpMessage } = useCarrotCompanionCarrotPlanMessage()

  const theme = useTheme()

  useEffect(() => {
    if (isGetThreadsSuccess && selectedThread == null && !showNewThreadForm) {
      if (threadParam) {
        const linkedThread = threadsData.threads.find((t) => t.threadId === parseInt(threadParam, 10))
        if (!linkedThread) {
          history.replace("/messages")
          setSelectedThread(threadsData.threads[0])
        }
        setSelectedThread(linkedThread)
      } else if (threadsData.threads[0]) {
        setSelectedThread(threadsData.threads[0])
      } else {
        setSelectedThread({ threadId: -1 })
      }
    }

    if (selectedThread?.threadId === cpMessage?.thread?.threadId) {
      updateCarrotPlanMessageDismissTimestamp()
    }
  }, [
    threadParam,
    selectedThread,
    setSelectedThread,
    showNewThreadForm,
    isGetThreadsSuccess,
    history,
    threadsData,
    cpMessage
  ])

  const mapThreads = useCallback(
    (threads: InAppMessagingThread[]) => {
      return threads.map((thread: InAppMessagingThread) => {
        return (
          <ThreadCardContainer
            key={thread.threadId}
            onClick={() => {
              setSelectedThread(thread)
              setShowNewThreadForm(false)
              setShowOnlyThreadsList(false)
            }}
          >
            <ThreadSideTick active={selectedThread?.threadId === thread.threadId && !showNewThreadForm} />
            <Stack
              direction="row"
              spacing={theme.spacing(theme.tokens.spacing.sm)}
              width="100%"
              sx={{ overflow: "hidden", backgroundColor: (theme) => theme.palette.background.paper }}
            >
              <ThreadSubjectAndDate
                thread={{
                  date: intl.formatDate(thread.updatedAt, { month: "short", day: "numeric" }),
                  subject: thread.subject
                }}
              />
              {thread.hasUnreadMessages && (
                <Box
                  sx={{
                    display: "flex",
                    inlineSize: "9px",
                    justifyContent: "flex",
                    alignItems: "center",
                    marginBlockStart: "4px"
                  }}
                >
                  <Box
                    sx={{
                      display: "block",
                      backgroundColor: (theme) => theme.palette.primary.accent,
                      blockSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      inlineSize: (theme) => theme.spacing(theme.tokens.spacing.xs),
                      borderRadius: "50%"
                    }}
                  />
                </Box>
              )}
            </Stack>
          </ThreadCardContainer>
        )
      })
    },
    [
      selectedThread?.threadId,
      setSelectedThread,
      showNewThreadForm,
      setShowNewThreadForm,
      setShowOnlyThreadsList,
      intl,
      theme
    ]
  )

  const threadsListScrollRef = useRef(null)
  function scrollThreadsListToTop(): void {
    threadsListScrollRef.current.scrollIntoView()
  }

  return (
    <>
      {isGetThreadsLoading && <CenteredLoadingIndicator />}
      {isGetThreadsError && <MessagingErrorContainer />}
      {isGetThreadsSuccess && (
        <Grid container>
          <Grid
            item
            xs={12}
            lg={4}
            sx={(theme) => ({
              display: hideColumn && !showOnlyThreadsList ? "none" : "block",
              overflowY: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.tokens.borderRadius.md
            })}
          >
            <StyledThreadsList>
              <div ref={threadsListScrollRef} />
              <Box component="nav">
                {mapThreads(threadsData.threads)}
                <DefaultThreadCard />
              </Box>
            </StyledThreadsList>
            <NewMessageButtonContainer
              disabled={showNewThreadForm}
              onClick={() => {
                setShowOnlyThreadsList(false)
                setShowNewThreadForm(true)
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            lg={8}
            sx={{
              minInlineSize: { xs: "100%", lg: "385px" },
              display: hideColumn && showOnlyThreadsList ? "none" : "block"
            }}
          >
            {showNewThreadForm ? (
              <InAppMessagingCreateNewThreadForm
                newestThread={!threadsData.threads[0]?.threadId ? { threadId: -1 } : threadsData.threads[0]}
                scrollThreadsListToTop={scrollThreadsListToTop}
              />
            ) : selectedThread?.threadId === -1 ? (
              <DefaultThreadMessage />
            ) : (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  blockSize: { xs: "calc(100vh - 216px)", sm: "calc(100vh - 248px)" }
                }}
              >
                <Box
                  sx={{
                    blockSize: "100%",
                    marginBlockEnd: "16px",
                    overflowX: "hidden",
                    overflowY: "auto",
                    marginInlineStart: { lg: "16px" }
                  }}
                >
                  <ThreadMessagesContainer />
                </Box>
                <MessageReplyContainer thread={selectedThread} scrollThreadsListToTop={scrollThreadsListToTop} />
              </Box>
            )}
          </Grid>
        </Grid>
      )}
    </>
  )
}

export default MemberMessagesContainer
